import { useAppDispatch, useAppSelector } from 'app/state'
import useAccountSpatialExtensionInfo from 'features/account/state/useAccountSpatialExtensionInfo'
import { useCallback, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  addSubscriptionRequested,
  checkIfDatasetHasBeenRequested,
  getActiveSubscriptions,
  getSubscribedSamples,
  getSubscriptionsRequested,
  removeSubscriptionRequested,
  subscribeDataset,
  subscribeSample
} from '../../api/subscriptions'

import { RequestStatus } from 'features/common'
import {
  setCurrentTable,
  setCurrentTableDataPreviewStatus,
  setCurrentTableStatus,
  setDataExplorerTreeStatus,
  setExpandedNodes
} from 'features/data-explorer/state/dataExplorerSlice'
import { setSubscriptionsStatus } from 'features/data-observatory/state/dataObservatorySlice'
import { useNavigate } from 'react-router-dom'
import {
  CatalogDataset,
  CatalogEntityTypes,
  CatalogSubscriptionRequestStatus,
  CatalogSubscriptionStatus,
  CatalogSubscriptionType
} from '../../types'
import { CatalogSubscriptionActionsContext, CatalogSubscriptionDataContext } from './context'
import { useConnections } from 'features/connections/hooks/connectionsHooks'

const EXPIRATION_THRESHOLD_DAYS = 30

const retryOpts = {
  retry: 5,
  retryDelay: 1000
}

export function SubscriptionsProvider({
  children,
  publicView = false
}: {
  children: React.ReactNode
  publicView?: boolean
}) {
  const queryClient = useQueryClient()
  // Due to a wrong connection management, it should be changed when connection sagas will be refactored
  useConnections({ refetchOnWindowFocus: false })

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const me = useAppSelector((state) => state.users.me)

  const invalidateDataExplorerTree = useCallback(
    (dataset: CatalogDataset, subscriptionType: CatalogSubscriptionType) => {
      dispatch(setSubscriptionsStatus(RequestStatus.Idle))
      dispatch(setCurrentTableDataPreviewStatus(RequestStatus.Idle))
      dispatch(setCurrentTableStatus(RequestStatus.Idle))
      dispatch(setCurrentTable(null))
      dispatch(setDataExplorerTreeStatus(RequestStatus.Idle))
      const type = subscriptionType === CatalogSubscriptionType.DATA ? 'subscriptions' : 'samples'
      const slug =
        subscriptionType === CatalogSubscriptionType.DATA ? dataset.slug : dataset.sampleInfo?.slug ?? dataset.slug
      const provider =
        subscriptionType === CatalogSubscriptionType.DATA
          ? dataset.providerId
          : dataset.sampleInfo?.providerId ?? dataset.providerId
      const newNodes = [provider, `${provider}.${type}`, `${provider}.${type}.${slug}`]
      dispatch(setExpandedNodes(newNodes))
    },
    [dispatch]
  )

  const { project, cartoDWAccountProject, dataProject, dataset, spatialExtensionConnection } =
    useAccountSpatialExtensionInfo(publicView)

  const { data: requested } = useQuery({
    queryKey: ['get-requested-subscriptions'],
    queryFn: () => getSubscriptionsRequested(),
    keepPreviousData: true,
    enabled: !publicView
  })

  const { data: actives, isFetching: isSubscriptionsLoading } = useQuery({
    queryKey: ['get-active-subscriptions'],
    queryFn: () => getActiveSubscriptions(),
    keepPreviousData: true,
    enabled: !publicView,
    ...retryOpts
  })

  const { data: samples, isFetching: isSamplesLoading } = useQuery<string[]>({
    queryKey: ['get-subscribed-samples'],
    keepPreviousData: true,
    queryFn: async () =>
      await getSubscribedSamples({
        connection: spatialExtensionConnection?.name as string,
        project,
        dataProject,
        dataset
      }),
    enabled: !publicView && Boolean(cartoDWAccountProject) && Boolean(spatialExtensionConnection?.name),
    ...retryOpts
  })

  useEffect(() => {
    const dirty = requested?.filter((r) => actives?.some((s) => s.datasetId === r))
    if (dirty?.length) {
      dirty.forEach((d) => {
        removeSubscriptionRequested({ dataset: d })
      })
      queryClient.invalidateQueries({ queryKey: ['get-requested-subscriptions'] })
    }
  }, [actives, requested, queryClient])

  const requestAccessSampleMutation = useMutation((dataset: CatalogDataset) => subscribeSample({ dataset }), {
    onSuccess: async (_, _dataset) => {
      invalidateDataExplorerTree(_dataset, CatalogSubscriptionType.SAMPLE)
      await queryClient.invalidateQueries({ queryKey: ['get-subscribed-samples'] })
    }
  })

  const requestSubscriptionMutation = useMutation(
    (params: {
      dataset: CatalogDataset
      requestStatus: CatalogSubscriptionRequestStatus
      email: string
      datasetType: CatalogEntityTypes
      hasCDWAccount: boolean
    }) => subscribeDataset(params),
    {
      onSuccess: async (_, { dataset, requestStatus }) => {
        if (dataset.licenseId !== 'public' || !cartoDWAccountProject) {
          if (requestStatus === CatalogSubscriptionRequestStatus.ACTIVE) {
            addSubscriptionRequested({ dataset })
          } else if (requestStatus === CatalogSubscriptionRequestStatus.CANCEL) {
            removeSubscriptionRequested({ dataset })
          }
        }
        invalidateDataExplorerTree(dataset, CatalogSubscriptionType.DATA)
        await queryClient.invalidateQueries({ queryKey: ['get-requested-subscriptions'] })
        await queryClient.invalidateQueries({ queryKey: ['get-active-subscriptions'] })
      }
    }
  )

  const checkIfSubscriptionRequested = useCallback((dataset: CatalogDataset) => {
    return checkIfDatasetHasBeenRequested(dataset.id)
  }, [])

  const getActiveSubscription = useCallback(
    (dataset: CatalogDataset) => {
      return actives?.find((d) => d.datasetId === dataset?.id)
    },
    [actives]
  )

  const checkIfSubscribed = useCallback(
    (dataset: CatalogDataset) => {
      return Boolean(getActiveSubscription(dataset))
    },
    [getActiveSubscription]
  )

  const getSubscriptionExpiration = useCallback(
    (dataset: CatalogDataset) => {
      const exp = getActiveSubscription(dataset)?.expiresAt
      if (exp) {
        return new Date(exp)
      }
      return exp
    },
    [getActiveSubscription]
  )

  const getSubscriptionExpirationLeft = useCallback(
    (dataset: CatalogDataset) => {
      const expires = getSubscriptionExpiration(dataset)
      if (!expires) {
        return Number.MAX_SAFE_INTEGER
      }
      const today = new Date()
      const target = new Date(expires)
      today.setHours(0, 0, 0, 0)
      target.setHours(0, 0, 0, 0)
      const diff = target.getTime() - today.getTime()
      return Math.ceil(diff / (1000 * 3600 * 24))
    },
    [getSubscriptionExpiration]
  )

  const getSubscriptionStatus = useCallback(
    (dataset?: CatalogDataset) => {
      if (!dataset) {
        return null
      }
      if (getSubscriptionExpirationLeft(dataset) <= EXPIRATION_THRESHOLD_DAYS) {
        return CatalogSubscriptionStatus.EXPIRES_SOON
      }
      if (checkIfSubscribed(dataset)) {
        return CatalogSubscriptionStatus.SUBSCRIBED
      }

      if (checkIfSubscriptionRequested(dataset)) {
        return CatalogSubscriptionStatus.REQUESTED
      }

      return null
    },
    [checkIfSubscriptionRequested, getSubscriptionExpirationLeft, checkIfSubscribed]
  )

  const sendSampleAccessRequest = useCallback(
    (dataset: CatalogDataset) => {
      return requestAccessSampleMutation.mutate(dataset)
    },
    [requestAccessSampleMutation]
  )

  const sendSubscriptionRequest = useCallback(
    (dataset: CatalogDataset, status: CatalogSubscriptionRequestStatus = CatalogSubscriptionRequestStatus.ACTIVE) => {
      const email = me?.email || ''
      const datasetType = dataset.isGeography ? CatalogEntityTypes.Geography : CatalogEntityTypes.Dataset
      return requestSubscriptionMutation.mutate({
        dataset,
        requestStatus: status,
        email,
        datasetType,
        hasCDWAccount: Boolean(cartoDWAccountProject)
      })
    },
    [requestSubscriptionMutation, me, cartoDWAccountProject]
  )

  const navigateToDataExplorer = useCallback(
    (dataset: CatalogDataset, subscriptionType: CatalogSubscriptionType) => {
      const type = subscriptionType === CatalogSubscriptionType.DATA ? 'subscriptions' : 'samples'
      const slug =
        subscriptionType === CatalogSubscriptionType.DATA ? dataset.slug : dataset.sampleInfo?.slug ?? dataset.slug
      const provider =
        subscriptionType === CatalogSubscriptionType.DATA
          ? dataset.providerId
          : dataset.sampleInfo?.providerId ?? dataset.providerId
      invalidateDataExplorerTree(dataset, subscriptionType)
      navigate(`/data/observatory/${provider}/${type}/${slug.replace(/\./g, '/')}`)
    },
    [invalidateDataExplorerTree, navigate]
  )

  const actions = {
    navigateToDataExplorer,
    sendSampleAccessRequest,
    sendSubscriptionRequest,
    checkIfSubscriptionRequested,
    checkIfSubscribed,
    getSubscriptionStatus,
    getSubscriptionExpirationLeft,
    getSubscriptionExpiration
  }

  const values = {
    cartoDWAccountProject,
    publicView,
    actives,
    samples,
    requested,
    isSubscriptionsLoading,
    isSamplesLoading
  }
  return (
    <CatalogSubscriptionActionsContext.Provider value={actions}>
      <CatalogSubscriptionDataContext.Provider value={values}>{children}</CatalogSubscriptionDataContext.Provider>
    </CatalogSubscriptionActionsContext.Provider>
  )
}
